import { useState } from 'react';
import { ErrorAlert, Field, TextareaField, SelectField } from './ui';
import { cn, ui } from '../lib/ui';

const WHO_CAN_OPTIONS = [
  { value: 'all_members', label: 'All members' },
  { value: 'moderators_only', label: 'Moderators only' }
];

const APPROVAL_OPTIONS = [
  { value: 'auto', label: 'Automatic' },
  { value: 'moderator_review', label: 'Moderator review' }
];

export function emptyGroupForm() {
  return {
    name: '',
    description: '',
    whoCanPost: 'all_members',
    whoCanInvite: 'all_members',
    whoCanMessage: 'all_members',
    memberApproval: 'auto'
  };
}

export function groupToForm(group) {
  const s = group.settings || {};
  return {
    name: group.name || '',
    description: group.description || '',
    whoCanPost: s.whoCanPost || 'all_members',
    whoCanInvite: s.whoCanInvite || 'all_members',
    whoCanMessage: s.whoCanMessage || 'all_members',
    memberApproval: s.memberApproval || 'auto'
  };
}

function buildPayload(form) {
  return {
    name: form.name.trim(),
    description: form.description.trim(),
    settings: {
      whoCanPost: form.whoCanPost,
      whoCanInvite: form.whoCanInvite,
      whoCanMessage: form.whoCanMessage,
      memberApproval: form.memberApproval
    }
  };
}

export default function GroupForm({ initial, submitLabel, onSubmit, onCancel, showSettings = true }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form.name.trim()) return;
    setError(null);
    setBusy(true);
    try {
      await onSubmit(buildPayload(form));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <ErrorAlert error={error} />
      <Field
        label="Name"
        required
        value={form.name}
        onChange={(e) => set({ name: e.target.value })}
        maxLength={100}
      />
      <TextareaField
        label="Description"
        rows={2}
        value={form.description}
        onChange={(e) => set({ description: e.target.value })}
        maxLength={500}
      />
      {showSettings && (
        <>
          <SelectField
            label="Who can post"
            value={form.whoCanPost}
            onChange={(e) => set({ whoCanPost: e.target.value })}
          >
            {WHO_CAN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </SelectField>
          <SelectField
            label="Who can invite"
            value={form.whoCanInvite}
            onChange={(e) => set({ whoCanInvite: e.target.value })}
          >
            {WHO_CAN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </SelectField>
          <SelectField
            label="Who can message"
            value={form.whoCanMessage}
            onChange={(e) => set({ whoCanMessage: e.target.value })}
          >
            {WHO_CAN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </SelectField>
          <SelectField
            label="Member approval"
            value={form.memberApproval}
            onChange={(e) => set({ memberApproval: e.target.value })}
          >
            {APPROVAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </SelectField>
        </>
      )}
      <div className={cn(ui.row, 'mt-4')}>
        <button type="submit" className={ui.btnPrimary} disabled={busy || !form.name.trim()}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className={ui.btnSecondary} disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
